import React, { useState } from 'react'
import './index.css'

export interface AttendanceModalProps {
  isOpen: boolean
  isLoading?: boolean
  onClose: () => void
  onOutcomeSelected: (outcome: string) => Promise<void>
  onNoSaleSubmit?: (data: NoSaleFormData) => Promise<void>
}

export interface NoSaleFormData {
  reasonCode: string
  productName?: string
  sizeWanted?: string
  colorWanted?: string
  note?: string
}

/**
 * AttendanceModal
 * Modal for completing seller service with outcome selection
 * If "no_sale" selected, expands to show form
 */
export const AttendanceModal: React.FC<AttendanceModalProps> = ({
  isOpen,
  isLoading,
  onClose,
  onOutcomeSelected,
  onNoSaleSubmit,
}) => {
  const [activeStep, setActiveStep] = useState<'outcomes' | 'no-sale-form'>('outcomes')
  const [noSaleForm, setNoSaleForm] = useState<NoSaleFormData>({
    reasonCode: '',
    productName: '',
    sizeWanted: '',
    colorWanted: '',
    note: '',
  })
  const [formError, setFormError] = useState<string | null>(null)

  if (!isOpen) return null

  const handleOutcomeClick = async (outcome: string) => {
    if (outcome === 'no_sale') {
      setActiveStep('no-sale-form')
    } else {
      try {
        await onOutcomeSelected(outcome)
        setActiveStep('outcomes')
        onClose()
      } catch (error) {
        console.error('Erro ao selecionar outcome:', error)
      }
    }
  }

  const handleNoSaleFormChange = (field: keyof NoSaleFormData, value: string) => {
    setNoSaleForm((prev) => ({ ...prev, [field]: value }))
    setFormError(null)
  }

  const handleNoSaleFormSubmit = async () => {
    // Validate required field
    if (!noSaleForm.reasonCode) {
      setFormError('Selecione um motivo')
      return
    }

    try {
      await onNoSaleSubmit?.(noSaleForm)
      await onOutcomeSelected('no_sale')
      setActiveStep('outcomes')
      setNoSaleForm({
        reasonCode: '',
        productName: '',
        sizeWanted: '',
        colorWanted: '',
        note: '',
      })
      onClose()
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Erro ao salvar')
    }
  }

  const handleBackToOutcomes = () => {
    setActiveStep('outcomes')
    setNoSaleForm({
      reasonCode: '',
      productName: '',
      sizeWanted: '',
      colorWanted: '',
      note: '',
    })
    setFormError(null)
  }

  return (
    <div className="attendance-modal-overlay">
      <div className="attendance-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>Encerrar Atendimento</h2>
          <button className="close-btn" onClick={onClose} disabled={isLoading}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {activeStep === 'outcomes' && (
            <div className="outcomes-step">
              <p className="step-description">Como foi o atendimento?</p>

              <div className="outcome-buttons">
                {/* Sale Completed */}
                <button
                  className="outcome-btn outcome-success"
                  onClick={() => handleOutcomeClick('sale_completed')}
                  disabled={isLoading}
                >
                  <span className="outcome-icon">✓</span>
                  <span className="outcome-text">Vendeu</span>
                </button>

                {/* Pending Checkout */}
                <button
                  className="outcome-btn outcome-info"
                  onClick={() => handleOutcomeClick('pending_checkout')}
                  disabled={isLoading}
                >
                  <span className="outcome-icon">🛒</span>
                  <span className="outcome-text">Enviou Carrinho Para Pagar Depois</span>
                </button>

                {/* No Sale */}
                <button
                  className="outcome-btn outcome-warning"
                  onClick={() => handleOutcomeClick('no_sale')}
                  disabled={isLoading}
                >
                  <span className="outcome-icon">✗</span>
                  <span className="outcome-text">Não Vendeu</span>
                </button>

                {/* Abandoned */}
                <button
                  className="outcome-btn outcome-danger"
                  onClick={() => handleOutcomeClick('abandoned')}
                  disabled={isLoading}
                >
                  <span className="outcome-icon">👤</span>
                  <span className="outcome-text">Cliente Desistiu</span>
                </button>
              </div>
            </div>
          )}

          {activeStep === 'no-sale-form' && (
            <div className="no-sale-form-step">
              <div className="form-back">
                <button className="back-btn" onClick={handleBackToOutcomes} disabled={isLoading}>
                  ← Voltar
                </button>
              </div>

              <h3>Por que não vendeu?</h3>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-group">
                <label htmlFor="reasonCode">Motivo *</label>
                <select
                  id="reasonCode"
                  value={noSaleForm.reasonCode}
                  onChange={(e) => handleNoSaleFormChange('reasonCode', e.target.value)}
                  disabled={isLoading}
                  className="form-input"
                >
                  <option value="">-- Selecione um motivo --</option>
                  <option value="size_unavailable">Tamanho indisponível</option>
                  <option value="color_unavailable">Cor indisponível</option>
                  <option value="price_too_high">Preço muito alto</option>
                  <option value="did_not_find_product">Não encontrou o produto</option>
                  <option value="just_browsing">Apenas navegando</option>
                  <option value="customer_left">Cliente saiu</option>
                  <option value="other">Outro</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="productName">Produto</label>
                <input
                  id="productName"
                  type="text"
                  placeholder="Qual produto?"
                  value={noSaleForm.productName}
                  onChange={(e) => handleNoSaleFormChange('productName', e.target.value)}
                  disabled={isLoading}
                  className="form-input"
                />
              </div>

              <div className="form-group-row">
                <div className="form-group">
                  <label htmlFor="sizeWanted">Tamanho</label>
                  <input
                    id="sizeWanted"
                    type="text"
                    placeholder="Ex: M, G, 42"
                    value={noSaleForm.sizeWanted}
                    onChange={(e) => handleNoSaleFormChange('sizeWanted', e.target.value)}
                    disabled={isLoading}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="colorWanted">Cor</label>
                  <input
                    id="colorWanted"
                    type="text"
                    placeholder="Ex: Preto, Azul"
                    value={noSaleForm.colorWanted}
                    onChange={(e) => handleNoSaleFormChange('colorWanted', e.target.value)}
                    disabled={isLoading}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="note">Observação</label>
                <textarea
                  id="note"
                  placeholder="Deixe uma nota adicional..."
                  value={noSaleForm.note}
                  onChange={(e) => handleNoSaleFormChange('note', e.target.value)}
                  disabled={isLoading}
                  className="form-input form-textarea"
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {activeStep === 'no-sale-form' && (
            <button className="btn btn-secondary" onClick={handleBackToOutcomes} disabled={isLoading}>
              Cancelar
            </button>
          )}
          {activeStep === 'no-sale-form' && (
            <button
              className="btn btn-primary"
              onClick={handleNoSaleFormSubmit}
              disabled={isLoading || !noSaleForm.reasonCode}
            >
              {isLoading ? 'Salvando...' : 'Salvar'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isLoading}
            style={{ display: activeStep === 'outcomes' ? 'block' : 'none' }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
